import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';

import styles from '../../styles/views/components/Dropdown.module.scss';

const dropdownFont = {
  fontSize: '16px',
  color: styles.colorAztec,
  fontFamily: styles.fontFamilyMain,
};

const Dropdown = ({
  placeholder,
  options,
  selectedValue,
  onSelect,
  showArrow,
  isError,
  errorMsg,
  isSearchable,
  isDisabled,
  noOptionsMessage,
  label,
  className,
  extraStyles,
  labelField,
  valueField,
  onCreateNewOption,
  isAllowAddNewOption,
  defaultValue,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const customStyles = {
    container: defaultStyles => ({
      ...defaultStyles,
      width: '100%',
      marginTop: label ? '-11px' : 0,
      padding: '0',
    }),
    control: (defaultStyles, state) => ({
      ...defaultStyles,
      ...dropdownFont,
      width: '100%',
      display: 'flex',
      backgroundColor: !state.isDisabled ? styles.colorWhite : styles.colorAlabaster,
      border: 'none',
      boxSizing: 'border-box',
      boxShadow: 'none',
      minHeight: '36px',
      height: '36px',
      '&:hover': {
        boxShadow: 'none',
      },
      borderRadius: 0,
    }),
    placeholder: defaultStyles => ({
      ...defaultStyles,
      ...dropdownFont,
      color: isError ? styles.colorRed : (isSearchable ? styles.colorAlto : styles.colorAztec),
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    }),
    menu: defaultStyles => ({
      ...defaultStyles,
      borderRadius: 0,
      boxSizing: 'content-box',
      boxShadow: '0px 5px 5px rgba(0, 0, 0, 0.05)',
      border: `1px solid ${styles.colorAztec}`,
      width: '100%',
      margin: '0 0 0 -1px',
      zIndex: 3,
    }),
    menuList: defaultStyles => ({
      ...defaultStyles,
      padding: 0,
      maxHeight: '120px',
      cursor: 'pointer',
      '&::-webkit-scrollbar': {
        width: '7px',
        backgroundColor: '#f9f9fd',
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '3px',
      },
    }),
    option: (defaultStyles, state) => ({
      ...defaultStyles,
      ...dropdownFont,
      backgroundColor: state.isFocused ? styles.colorAlabaster : styles.colorWhite,
      padding: '10px 11px',
    }),
    dropdownIndicator: (defaultStyles, state) => ({
      ...defaultStyles,
      color: !state.isDisabled ? styles.colorAztec : styles.colorAlto,
      transition: 'all .2s ease',
      transform: isMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    }),
    singleValue: defaultStyles => ({
      ...defaultStyles,
      ...dropdownFont,
    }),
  };

  const getComponents = () => {
    let components = { IndicatorSeparator: () => null };
    if (!showArrow) {
      components = {
        ...components,
        DropdownIndicator: () => null,
      };
    }
    return components;
  };

  const memoizedValue = useMemo(() => options.find(item => item[valueField] === selectedValue), [selectedValue, options, valueField]);

  const fieldsetClasses = [];
  if (!label) fieldsetClasses.push(styles.noLabel);
  if (isDisabled) fieldsetClasses.push(styles.disabled);
  if (isError) fieldsetClasses.push(styles.error);

  const commonProps = {
    styles: customStyles,
    placeholder,
    options,
    value: !selectedValue ? defaultValue : memoizedValue,
    isDisabled,
    isSearchable,
    noOptionsMessage: () => noOptionsMessage,
    components: getComponents(),
    onMenuOpen: () => setIsMenuOpen(true),
    onMenuClose: () => setIsMenuOpen(false),
    onChange: item => onSelect(item[valueField]),
    getOptionLabel: option => option[labelField],
    getOptionValue: option => option[valueField],
  };

  return (
    <div className={[styles.container, className].join(' ')}>
      <fieldset className={fieldsetClasses.join(' ')} style={extraStyles}>
        {label && <legend>{label}</legend>}
        {!isAllowAddNewOption
          ? (
            <Select
              {...commonProps}
            />
          )
          : (
            <CreatableSelect
              onCreateOption={onCreateNewOption}
              renderRemoveIcon={false}
              {...commonProps}
            />
          )}
      </fieldset>
      {isError ? (
        <div className={styles.error}>
          {errorMsg}
        </div>
      ) : null
      }
    </div>
  );
};

Dropdown.propTypes = {
  onSelect: PropTypes.func.isRequired,
  onCreateNewOption: PropTypes.func,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  })),
  selectedValue: PropTypes.string,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  noOptionsMessage: PropTypes.string,
  showArrow: PropTypes.bool,
  isError: PropTypes.bool,
  errorMsg: PropTypes.string,
  label: PropTypes.string,
  className: PropTypes.string,
  extraStyles: PropTypes.oneOfType([PropTypes.object, PropTypes.oneOf([null])]),
  labelField: PropTypes.string,
  valueField: PropTypes.string,
  isAllowAddNewOption: PropTypes.bool,
  defaultValue: PropTypes.string,
};

Dropdown.defaultProps = {
  placeholder: '',
  options: [],
  selectedValue: '',
  isDisabled: false,
  isSearchable: false,
  noOptionsMessage: 'No options',
  showArrow: true,
  isError: false,
  errorMsg: 'Please, choose an option',
  label: '',
  className: '',
  extraStyles: null,
  labelField: 'label',
  valueField: 'value',
  onCreateNewOption: () => { },
  isAllowAddNewOption: false,
  defaultValue: '',
};

export default Dropdown;
